// client/src/components/ofertas/OfertaRow.js
import React from 'react';
import PropTypes from 'prop-types';
import { MoreVertical, Tag, CheckCircle, XCircle } from 'lucide-react'; // Ícones relevantes
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency as formatCurrencyUtil } from '../../utils/formatters';

const OfertaRow = ({ oferta, onOpenActions, loading }) => {
    // ✅ ACHADO: formatCurrency era fixo em BRL, sem ler
    // configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const formatCurrency = (value) => formatCurrencyUtil(value || 0, currency, locale);

    // Tratamento básico se a oferta for inválida
    if (!oferta) {
        return (<tr><td colSpan="6" className="td-empty">Dados da oferta indisponíveis.</td></tr>);
    }

    // Calcula o valor base para exibição (pode ser ajustado conforme a lógica exata)
    const calcularValorBase = (of) => {
        if (of.tipoDePrecificacao === 'Fixo') return of.valorFixo;
        if (of.tipoDePrecificacao === 'Por Pacote' && of.pacotesDePreco?.length > 0) {
            // Mostra o menor valor dos pacotes como referência "A partir de"
            const minValor = Math.min(...of.pacotesDePreco.map(p => p.valor || Infinity));
            return minValor === Infinity ? 0 : minValor;
        }
        // Para 'Sob Consulta' ou 'A Partir de' sem valor fixo/pacote, ou Combos sem valor fixo
        return of.valorTotalEstimado || 0; // Usa o estimado como fallback
    };

    const valorBase = calcularValorBase(oferta);

    // Determina o texto e estilo do Status
    const statusInfo = oferta.ativo
        ? { text: 'Ativa', className: 'status-active', Icon: CheckCircle }
        : { text: 'Inativa', className: 'status-inactive', Icon: XCircle };

    return (
        <tr className="table-row-hover">
            {/* Nome */}
            <td className="td-name">
                <span className="table-icon-wrapper">
                    <Tag size={16} /> {/* Ou Gift */}
                </span>
                {oferta.nome || 'Oferta sem nome'}
            </td>
            {/* Tipo */}
            <td>
                <span className={`badge badge-${oferta.tipo?.toLowerCase()}`}>{oferta.tipo || 'N/D'}</span>
            </td>
            {/* Precificação */}
            <td>{oferta.tipoDePrecificacao || 'N/D'}</td>
            {/* Valor Base */}
            <td className="text-center">
                 {oferta.tipoDePrecificacao === 'Sob Consulta' ? (
                     <span className="text-gray-500 text-sm">Sob Consulta</span>
                 ) : (
                     formatCurrency(valorBase) + (oferta.tipoDePrecificacao === 'Por Pacote' ? '*' : '') // Adiciona * para indicar "a partir de"
                 )}
            </td>
            {/* Status */}
            <td className="text-center">
                 <span className={`status-badge ${statusInfo.className}`}>
                    <statusInfo.Icon size={14} className="mr-1"/>
                    {statusInfo.text}
                 </span>
            </td>
            {/* Ações */}
            <td className="text-right">
                <button
                    onClick={() => onOpenActions(oferta)}
                    title="Ações"
                    className="action-button-icon text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                >
                    <MoreVertical size={18} />
                </button>
            </td>
        </tr>
    );
};

// PropTypes para validação das props
OfertaRow.propTypes = {
    oferta: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        nome: PropTypes.string,
        tipo: PropTypes.string,
        tipoDePrecificacao: PropTypes.string,
        valorFixo: PropTypes.number,
        valorTotalEstimado: PropTypes.number,
        pacotesDePreco: PropTypes.arrayOf(PropTypes.shape({
            valor: PropTypes.number
        })),
        ativo: PropTypes.bool,
        // Adicionar outros campos se usados diretamente na Row
    }).isRequired,
    onOpenActions: PropTypes.func.isRequired,
    loading: PropTypes.bool // Loading geral (geralmente do delete)
};

export default OfertaRow;